import {API} from "@/middlewares/api";
import {createAsyncThunk} from "@reduxjs/toolkit";
import {updateUserDetails} from "./userSlice";

export const loadUser = createAsyncThunk("user/loadUser", async ({userId, mustValidateToken}, thunkAPI) => {
  try {
    const response = await API(`secure/v2/users/${userId}`, {method: "GET", mustValidateToken});

    return thunkAPI.fulfillWithValue(response);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const checkUserDetails = createAsyncThunk("user/checkUserDetails", async (userId, thunkAPI) => {
  try {
    const response = await API(`secure/v2/users/${userId}`);

    return thunkAPI.fulfillWithValue(response);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const loadMedicalInformation = createAsyncThunk(
  "user/loadMedicalInformation",
  async ({userId, mustValidateToken}, thunkAPI) => {
    try {
      const response = await API(`secure/medical/basic-medical/${userId}`, {method: "GET", mustValidateToken});

      return thunkAPI.fulfillWithValue(response);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const checkMedicalInformation = createAsyncThunk("user/checkMedicalInformation", async (userId, thunkAPI) => {
  try {
    const response = await API(`secure/medical/basic-medical/${userId}`);

    return thunkAPI.fulfillWithValue(response);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const saveThemePreference = createAsyncThunk("user/saveThemePreference", async (theme, thunkAPI) => {
  try {
    const response = await API("secure/users/save-theme-preference", {method: "POST", body: {theme}});

    return thunkAPI.fulfillWithValue(response);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const saveNotificationPreference = createAsyncThunk(
  "user/saveNotificationPreference",
  async (featureType, thunkAPI) => {
    try {
      const response = await API("secure/users/notify-about-feature", {method: "POST", body: {featureType}});

      return thunkAPI.fulfillWithValue(response);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const uploadProfileImage = createAsyncThunk("user/uploadProfileImage", async (file, thunkAPI) => {
  try {
    const response = await API("secure/users/upload-profile-picture", {method: "POST", body: {file}});

    if (response[0]?.success === "true") {
      const {user} = thunkAPI.getState();
      thunkAPI.dispatch(
        updateUserDetails({
          ...user.data,
          customerImage: file,
        }),
      );
    }

    return thunkAPI.fulfillWithValue(response);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const resetPassword = createAsyncThunk("user/resetPassword", async ({username, siteId}, thunkAPI) => {
  try {
    const response = await API("reset-password", {method: "POST", body: {username, siteId}});

    return thunkAPI.fulfillWithValue(response);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const checkEmailsExist = createAsyncThunk("user/checkEmailsExist", async (customerEmailAddress, thunkAPI) => {
  try {
    const response = await API("check-email", {method: "POST", body: {customerEmailAddress}});

    return thunkAPI.fulfillWithValue(response);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const updateCustomerProfile = createAsyncThunk("user/updateCustomerProfile", async (payload, thunkAPI) => {
  try {
    const response = await API("v2/users/update-customer-profile", {method: "POST", body: payload});

    return thunkAPI.fulfillWithValue(response);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const addCredit = createAsyncThunk("user/addCredit", async (payload, thunkAPI) => {
  try {
    const response = await API("/check-voucher", {method: "POST", body: payload});

    return thunkAPI.fulfillWithValue(response);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const deleteAccount = createAsyncThunk("user/deleteAccount", async (_, thunkAPI) => {
  try {
    const response = await API("secure/users/delete-user-profile");

    return thunkAPI.fulfillWithValue(response);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const activateCode = createAsyncThunk("user/activateCode", async (payload, thunkAPI) => {
  try {
    const response = await API("secure/users/activate-kit", {method: "POST", body: payload});

    return thunkAPI.fulfillWithValue(response);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const generateReferCode = createAsyncThunk("user/generateReferCode", async (payload, thunkAPI) => {
  try {
    const response = await API("secure/generate-refer-code");

    return thunkAPI.fulfillWithValue(response);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const loadReferCodeUses = createAsyncThunk("user/loadReferCodeUses", async (payload, thunkAPI) => {
  try {
    const response = await API("secure/get-refer-code-uses");

    return thunkAPI.fulfillWithValue(response);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const updateHealthCategoriesInterest = createAsyncThunk(
  "user/updateHealthCategoriesInterest",
  async (payload, thunkAPI) => {
    try {
      const response = await API("secure/users/update-health-categories", {method: "POST", body: payload});

      return thunkAPI.fulfillWithValue(response);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const loadCustomerVouchers = createAsyncThunk(
  "user/loadCustomerVouchers",
  async (mustValidateToken = true, thunkAPI) => {
    try {
      const response = await API("secure/get-vouchers/me", {method: "GET", mustValidateToken});

      return thunkAPI.fulfillWithValue(response);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const updateCustomerLogs = createAsyncThunk("user/updateCustomerLogs", async (payload, thunkAPI) => {
  try {
    const response = await API("secure/users/update-logs", {method: "POST", body: payload});

    return thunkAPI.fulfillWithValue(response);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const updateAppRating = createAsyncThunk("user/updateAppRating", async (payload, thunkAPI) => {
  try {
    const response = await API("secure/users/app-rating", {method: "POST", body: payload});

    return thunkAPI.fulfillWithValue(response);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const updateHealthCoachRating = createAsyncThunk("user/updateHealthCoachRating", async (payload, thunkAPI) => {
  try {
    const response = await API("secure/users/healthcoach-rating", {method: "POST", body: payload});

    return thunkAPI.fulfillWithValue(response);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});
